import { useImperativeHandle, useRef, useState } from 'react';
import type { FormEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { NgButton } from '../buttons/NgButton';
import { NgModal } from '../modals/NgModal';
import type { NgModalHandle } from '../modals/NgModal';
import { useEditForm } from '../forms/useEditForm';

export type EditValueDialogHandle = {
  showModal: (value?: string) => void;
};

export type EditValueDialogProps = {
  label: string;
  multiline?: boolean;
  onValueEdit?: (value: string) => void | Promise<void>;
  dialogHeader?: string;
  valueRequired?: boolean;
  ref?: Ref<EditValueDialogHandle>;
};

const FORM_ID = `edit-value-form-${crypto.randomUUID()}`;
const VALUE_INPUT_ID = `value-${crypto.randomUUID()}`;

export function EditValueDialog({
  label,
  multiline,
  onValueEdit,
  dialogHeader,
  valueRequired,
  ref,
}: EditValueDialogProps) {
  const { t } = useTranslation();
  const modalRef = useRef<NgModalHandle>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState('');
  const editForm = useEditForm<'value'>();

  useImperativeHandle(ref, () => ({
    showModal: (initialValue?: string) => {
      setValue(initialValue ?? '');
      editForm.reset({ value: initialValue ?? '' });
      modalRef.current?.show();
    },
  }));

  const effectiveHeader = dialogHeader ?? t('DataPanelItem_Dialog_EditValue_Header');

  const closeDialog = () => {
    modalRef.current?.hide();
  };

  const formSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const isValid = editForm.validate(() =>
      valueRequired && value.trim() === '' ? { value: [t('ValueRequired')] } : {},
    );
    if (!isValid) {
      return;
    }

    await onValueEdit?.(value);
    closeDialog();
  };

  const focusInput = () => {
    if (multiline) {
      textAreaRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  };

  const inputClassName = editForm.fieldCssClass('value', 'form-control');

  const handleChange = (newValue: string) => {
    setValue(newValue);
    editForm.notifyFieldInput('value', newValue);
  };

  return (
    <NgModal
      ref={modalRef}
      header={effectiveHeader}
      onShown={focusInput}
      bodyTemplate={
        <form id={FORM_ID} onSubmit={(event) => void formSubmit(event)}>
          <div style={{ minHeight: '7rem' }}>
            <label htmlFor={VALUE_INPUT_ID} className="label">
              {label}
            </label>

            {multiline ? (
              <textarea
                ref={textAreaRef}
                id={VALUE_INPUT_ID}
                className={inputClassName}
                rows={8}
                value={value}
                onChange={(event) => handleChange(event.target.value)}
                onBlur={() => editForm.notifyFieldBlur('value')}
              />
            ) : (
              <input
                ref={inputRef}
                id={VALUE_INPUT_ID}
                className={inputClassName}
                value={value}
                onChange={(event) => handleChange(event.target.value)}
                onBlur={() => editForm.notifyFieldBlur('value')}
              />
            )}

            {editForm.validationMessages('value').map((message, index) => (
              <div key={index} className="validation-message">
                {message}
              </div>
            ))}
          </div>
        </form>
      }
      footerTemplate={
        <>
          <NgButton submitButton form={FORM_ID} icon="fal fa-check-circle" text={t('Button_Save')} />
          <NgButton text={t('Button_Cancel')} outline onClick={closeDialog} />
        </>
      }
    />
  );
}
